import React, { useEffect, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAPI } from '../../API';
import { ActionTypes, useAppDispatch, useAppState } from '../../Store';
import { useTranslation } from '../../Translation';
import SidebarNav from '../../Components/SidebarNav/SidebarNav';
import useAuthenticated from '../Dashboard/useAuthenticated';
import Table from '../../Components/Table/Table';
import LoadingCircleTransparent from '../../Components/LoadingIndicators/LoadingCircleTransparent';
import { usePaginations } from '../../Hooks/usePaginations';
import { useDateFormat } from '../../Hooks/useDateFormat';
import Modal, { useModal } from '../../Components/Modal';
import { Button } from '../../Components/Buttons';
import { TextInput } from '../../Components/Inputs/TextInput';
import { useRefresh } from '../../Hooks/useRefresh';
import { PencilIcon, TrashIcon } from '@heroicons/react/20/solid';
import ClearAll from '../../Resources/Images/Files/ClearAll';
import Select from '../../Components/Inputs/Select';
import { badgeColorsChoices } from '../../Components/Badge';

export const useTagMappingForTable = (tags) => {
    const { translate } = useTranslation();
    const { formatDate } = useDateFormat();

    return tags.map((tag) => {
        // Create a new object without the data and is_deleted properties
        const { data, is_deleted, ...tagWithoutUnwantedProps } = tag;
        return {
            ...tagWithoutUnwantedProps,
            key: tag.id,
            created_at: formatDate(tag.created_at),
            color_translated: translate(`colors.${tag.color}`)
        };
    });
}

export default function Tags() {

    const { translate, language, setLanguage } = useTranslation();

    const { isAuthenticated, pathAfterLogin } = useAuthenticated();

    const api = useAPI();
    const appState = useAppState();
    const { sidebarSearch } = appState;

    const [tags, setTags] = useState([]);
    const [loading, setLoading] = useState(true);
    const pagination = usePaginations('name', 'asc');
    const { open, setOpen } = useModal();
    const { refreshId, refresh } = useRefresh();
    const { page, pageSize } = pagination;
    const [selectedTag, setSelectedTag] = useState(null);
    const navigate = useNavigate();
    useEffect(() => {
        pagination.setPage(1);
    }, [sidebarSearch]);

    useEffect(() => {
        const fetchContacts = async () => {
            try {
                setLoading(true);
                const response = await api.listTags(page, pageSize, pagination.sortBy, pagination.sortOrder, sidebarSearch);
                setTags(response.data.tags);
                pagination.setMetaData(response.data.meta);
            } catch (error) {
                console.error(error);
            } finally {
                setLoading(false);
            }
        }

        fetchContacts();
    }, [pagination.page, pagination.sortBy, pagination.sortOrder, refreshId, sidebarSearch]);

    const mappedTags = useTagMappingForTable(tags);

    const handleSort = ({ key }) => {
        if (pagination.sortBy === key) {
            pagination.setSortOrder(pagination.sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            pagination.setSortBy(key);
            pagination.setSortOrder('asc');
        }
        pagination.setPage(1);
    }

    const handleEdit = (tag) => {
        try {
            setOpen(true);
        } catch (error) {
            console.error(error);
        }
    }

    const handleDelete = async (tag) => {
        try {
            await api.deleteTag(tag.id);
            refresh();
        } catch (error) {
            console.error(error);
        }
    }

    return (
        <>
            {/* Place the Modal outside the main container */}
            <Modal
                open={open}
                onClose={() => setOpen(false)}
                title={selectedTag ? translate('tags.edit') : translate('tags.create')}
                description={''}
                children={<TagModal refresh={refresh} setOpen={setOpen} tag={selectedTag} setSelectedTag={setSelectedTag} open={open} />}
            />

            <div className="flex min-h-full w-full">
                <SidebarNav currentPage="tags">
                    {loading ? <div className="flex justify-center items-center h-full w-12">
                        <LoadingCircleTransparent />
                    </div> : (
                        <Table
                            title={translate('tags.title')}
                            description={translate('tags.description')}
                            data={mappedTags}
                            translationKey="tags.table"
                            columns={[{ key: 'name', type: 'badge', color: (row) => row.color }, 'created_at']}
                            pagination={pagination}
                            onSort={handleSort}
                            createButton={{
                                text: translate('tags.create'),
                                action: () => setOpen(true)
                            }}
                            onRowClick={(row) => {
                                navigate(`/tag/${row.id}`);
                            }}
                            dropdownGroups={[{
                                options: [{
                                    label: translate('edit'),
                                    icon: <PencilIcon aria-hidden="true" className="mr-3 size-5 text-gray-400 group-data-[focus]:text-gray-500" />,
                                    action: (row) => { setSelectedTag(row); setOpen(true) }
                                }
                                ]
                            }, {
                                options: [{
                                    label: translate('delete'),
                                    icon: <TrashIcon aria-hidden="true" className="mr-3 size-5 text-red-400 group-data-[focus]:text-gray-500" />,
                                    action: (row) => handleDelete(row)
                                }]
                            }]}
                        />
                    )}
                </SidebarNav>
            </div>
        </>
    );
}

export const TagModal = ({ refresh, setOpen, tag, setSelectedTag, open }) => {

    const [name, setName] = useState(tag ? tag.name : '');
    const [color, setColor] = useState(tag ? badgeColorsChoices.find(choice => choice.color === tag.color) : badgeColorsChoices.find(choice => choice.color === 'gray'));
    const { translate } = useTranslation();
    const api = useAPI();

    const handleCreate = async () => {
        try {
            await api.createTag(name, color.color);
            refresh();

            setOpen(false);
            setName('');
        } catch (error) {
            console.error(error);
        }
    }

    const handleUpdate = async () => {
        try {
            await api.updateTag(tag.id, name, color.color);
            refresh();
            setOpen(false);
        } catch (error) {
            console.error(error);
        }
    }

    useEffect(() => {
        if (!open) {
            setName('');
            setColor(badgeColorsChoices.find(choice => choice.color === 'gray'));
            setSelectedTag(null);
        }
    }, [open]);

    return (
        <div className="mt-4 overflow-visible">
            <TextInput id="name" name="name" label={translate('tags.name')} placeholder={translate('tags.name_placeholder')} value={name} onChange={(e) => setName(e.target.value)} />
            <div className="flex flex-col mt-4">
                <Select title={translate('tags.color')} options={badgeColorsChoices} selected={color} setSelected={setColor} />
            </div>
            <div className="flex justify-end mt-4">
                <Button disabled={!name || name.length === 0} onClick={tag ? handleUpdate : handleCreate}>{tag ? translate('update') : translate('create')}</Button>
            </div>
        </div>
    )
}